import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ExternalLink, QrCode, Megaphone, Eye, MousePointerClick, UserPlus, ScanLine, Pencil, ShieldCheck, Rocket, Copy, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TYPE_LABEL = { brand: "Brand", outfitter: "Outfitter", guide: "Guide", state: "State Partner" };
const VERIFY_LABEL = {
  pending: "Verification pending", verified_business: "Verified Brand", verified_retailer: "Verified Brand",
  verified_outfitter: "Verified Outfitter", verified_guide: "Verified Guide", official_state: "Official State Partner",
  unverified: "Unverified",
};

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="border border-saddle/20 bg-white rounded-xl p-4" data-testid={testid}>
      <Icon size={16} className="text-saddle" />
      <p className="font-serif text-3xl text-forest mt-2 tabular-nums">{value ?? 0}</p>
      <p className="text-xs text-charcoal/60 mt-0.5">{label}</p>
    </div>
  );
}

export default function PartnerDashboard() {
  const { isAuthed, ready } = useAuth();
  const { setAuthOpen } = useUI();
  const navigate = useNavigate();
  const [data, setData] = useState(undefined);
  const [stats, setStats] = useState({});
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!ready || !isAuthed) return;
    Promise.all([
      api.partnerOnboarding(),
      api.partnerMe().catch(() => ({ partner: null })),
      api.partnerAnalytics().catch(() => ({ stats: {} })),
    ]).then(([ob, me, an]) => {
      setStatus(ob);
      setData(me.partner ? me : null);
      setStats(an.stats || {});
    }).catch(() => setData(null));
  }, [ready, isAuthed]);

  if (!ready || (isAuthed && data === undefined)) return <Shell><div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div></Shell>;
  if (!isAuthed) return (
    <Shell><div className="max-w-md mx-auto text-center py-24" data-testid="dashboard-signin-gate">
      <h1 className="font-serif text-3xl text-forest">Partner Dashboard</h1>
      <p className="text-charcoal/60 mt-2">Sign in to view your partner dashboard.</p>
      <button onClick={() => setAuthOpen(true)} className="mt-5 bg-blaze text-white px-6 py-3 rounded-full font-semibold" data-testid="dashboard-signin-btn">Sign in</button>
    </div></Shell>
  );

  // Not yet a partner, or onboarding incomplete → guide into onboarding (never an empty dashboard)
  if (!data || !status?.has_business || !status?.onboarding_complete) {
    return (
      <Shell>
        <div className="max-w-lg mx-auto text-center py-20" data-testid="dashboard-onboarding-cta">
          <Rocket size={32} className="mx-auto text-blaze" />
          <h1 className="font-serif text-3xl text-forest mt-4">Let&apos;s finish setting up</h1>
          <p className="text-charcoal/60 mt-2">
            {status?.step === "subscribe" ? "Choose a plan to activate your partner profile."
              : status?.step === "profile" ? "Complete your profile to publish and unlock your dashboard."
              : "Complete onboarding to publish your HomeRange partner profile."}
          </p>
          <button onClick={() => navigate("/partner/onboarding")} className="mt-6 inline-flex items-center gap-2 bg-forest text-bone px-6 py-3 rounded-full font-semibold hover:bg-forest-2 transition-colors" data-testid="dashboard-continue-onboarding">
            Continue onboarding <ExternalLink size={15} />
          </button>
        </div>
      </Shell>
    );
  }

  const p = data.partner;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const profileUrl = origin + (p.public_path || "");
  const referralUrl = origin + (p.referral_link && p.referral_link.startsWith("/") ? p.referral_link : `/r/${p.referral_code}`);
  const qrPng = `${BACKEND}/api/partners/${p.id}/qr.png?box=14`;
  const verify = VERIFY_LABEL[p.verification_status] || "Unverified";
  const copy = (v, label) => { navigator.clipboard?.writeText(v); toast.success(`${label} copied`); };

  return (
    <Shell>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-[1100px] mx-auto">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-white border border-saddle/20 overflow-hidden grid place-items-center shrink-0">
              {data.business?.logo ? <img src={data.business.logo} alt="" className="w-full h-full object-cover" /> : <span className="font-serif text-2xl text-forest">{(p.name || "H")[0]}</span>}
            </div>
            <div>
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-blaze">Partner Dashboard</span>
              <h1 className="font-serif text-3xl text-forest" data-testid="dashboard-partner-name">{p.name}</h1>
              <p className="text-sm text-charcoal/60">{TYPE_LABEL[p.partner_type] || "Partner"} · <span className="inline-flex items-center gap-1"><ShieldCheck size={13} className="text-forest" />{verify}</span></p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/partner/publish")} className="inline-flex items-center gap-1.5 bg-blaze text-white px-4 py-2.5 rounded-full text-sm font-semibold hover:bg-blaze-dark transition-colors" data-testid="dashboard-publish-link"><Megaphone size={15} /> Publish</button>
            <a href={profileUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 bg-forest text-bone px-4 py-2.5 rounded-full text-sm font-semibold" data-testid="dashboard-view-public"><ExternalLink size={15} /> View public profile</a>
          </div>
        </div>

        {/* Completion */}
        <div className="mt-6 border border-saddle/20 bg-white rounded-xl p-5" data-testid="dashboard-completion">
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold text-forest">Profile {p.completion}% complete</span>
            <button onClick={() => navigate("/partner/edit")} className="text-blaze text-xs font-semibold inline-flex items-center gap-1" data-testid="dashboard-edit-profile"><Pencil size={12} /> Edit profile</button>
          </div>
          <div className="h-2 bg-paper rounded-full mt-2 overflow-hidden"><div className="h-full bg-blaze transition-all" style={{ width: `${p.completion}%` }} /></div>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between mt-6 mb-3">
          <h2 className="text-[11px] font-bold uppercase tracking-wide text-saddle">Performance</h2>
          <button onClick={() => navigate("/partner/analytics")} className="text-blaze text-xs font-semibold inline-flex items-center gap-1" data-testid="dashboard-analytics-link"><TrendingUp size={12} /> View full analytics</button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat icon={Eye} label="Profile views" value={stats.views} testid="stat-views" />
          <Stat icon={ScanLine} label="QR scans" value={stats.qr_scans} testid="stat-qr-scans" />
          <Stat icon={MousePointerClick} label="Referral clicks" value={stats.referral_clicks} testid="stat-referral-clicks" />
          <Stat icon={UserPlus} label="Referral signups" value={stats.signups} testid="stat-signups" />
        </div>

        <div className="grid lg:grid-cols-[1fr_300px] gap-6 mt-6">
          {/* Links */}
          <div className="border border-saddle/20 bg-white rounded-xl p-5 space-y-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-saddle">Your HomeRange URL</p>
              <div className="flex items-center gap-2 mt-1">
                <input readOnly value={profileUrl} className="flex-1 border border-saddle/25 bg-paper px-3 py-2 rounded-lg text-sm text-charcoal/80 truncate" data-testid="dashboard-profile-url" />
                <button onClick={() => copy(profileUrl, "URL")} className="p-2.5 border border-saddle/25 rounded-lg text-charcoal/70 hover:text-blaze" data-testid="dashboard-copy-url"><Copy size={15} /></button>
              </div>
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-saddle">Referral link</p>
              <div className="flex items-center gap-2 mt-1">
                <input readOnly value={referralUrl} className="flex-1 border border-saddle/25 bg-paper px-3 py-2 rounded-lg text-sm text-charcoal/80 truncate" data-testid="dashboard-referral-url" />
                <button onClick={() => copy(referralUrl, "Referral link")} className="p-2.5 border border-saddle/25 rounded-lg text-charcoal/70 hover:text-blaze" data-testid="dashboard-copy-referral"><Copy size={15} /></button>
              </div>
            </div>
            <Link to="/partner/marketing" className="inline-flex items-center gap-1.5 text-sm font-semibold text-blaze" data-testid="dashboard-marketing-link"><Megaphone size={15} /> Marketing tools & captions</Link>
          </div>

          {/* QR */}
          <div className="border border-saddle/20 bg-white rounded-xl p-4 text-center h-fit" data-testid="dashboard-qr">
            <div className="flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-saddle"><QrCode size={13} /> Your QR code</div>
            <img src={qrPng} alt="Partner QR code" className="w-full rounded-lg border border-saddle/15 mt-3" data-testid="dashboard-qr-image" />
            <a href={qrPng} download className="mt-3 inline-block text-xs font-semibold text-blaze">Download PNG</a>
          </div>
        </div>
      </motion.div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="bg-paper min-h-screen" data-testid="partner-dashboard">
      <SiteHeader />
      <main className="max-w-[1100px] mx-auto px-5 sm:px-8 py-10">{children}</main>
      <Footer />
    </div>
  );
}
